// ==============================|| REACT & THIRD-PARTY IMPORTS ||============================== //
import React from 'react';
import {
    Dialog,
    DialogContent,
    Button,
    Typography,
    Stack,
    Divider,
    Box,
    Tooltip,
    useTheme
} from '@mui/material';
import { alpha } from '@mui/material/styles';
import { DeleteFilled, RightOutlined } from '@ant-design/icons';

// ==============================|| LOCAL COMPONENTS & ICONS ||============================== //
import { MixedAssetsIcon } from './CommonFilemanagerIcon';

// ==============================|| CONTEXT & HOOKS ||============================== //
import { useFileManagerState } from 'hooks/useFileManager';
import { DeleteFileDialogProps } from 'types/CommonFileManager/commonFileManager';



export default function DeleteFileDialog({
    open: propOpen,
    onClose: propOnClose,
    onConfirm: propOnConfirm,
    fileName: propFileName,
    filePath: propFilePath,
    isMultiple: propIsMultiple = false,
    filesCount: propFilesCount = 0
}: DeleteFileDialogProps) {
    const state = useFileManagerState();

    const open = propOpen ?? state.isDeleteOpen;
    const onClose = propOnClose ?? (() => {
        state.setIsDeleteOpen(false);
    });
    const onConfirm = propOnConfirm ?? (async () => {
        const action = state.deleteConfig?.confirmAction;
        state.setIsDeleteOpen(false);
        if (action) await action();
    });
    const fileName = propFileName ?? state.deleteConfig?.fileName ?? '';
    const filePath = propFilePath ?? state.deleteConfig?.filePath ?? '';
    const isMultiple = propIsMultiple ?? state.deleteConfig?.isMultiple ?? false;
    const filesCount = propFilesCount ?? state.deleteConfig?.filesCount ?? 0;

    const theme = useTheme();

    return (
        <Dialog
            open={open}
            TransitionProps={{
                onExited: () => {
                    state.setDeleteConfig(null);
                }
            }}
            onClose={(event, reason) => {
                if (reason !== 'backdropClick' && reason !== 'escapeKeyDown') {
                    onClose();
                }
            }}
            fullWidth
            maxWidth={false}
            PaperProps={{
                sx: {
                    width: '550px',
                    borderRadius: '8px',
                    boxShadow: '0 12px 48px rgba(0, 0, 0, 0.08)',
                    p: 3,
                    overflow: 'hidden'
                }
            }}
        >
            <DialogContent sx={{ p: 0, display: 'flex', flexDirection: 'column', alignItems: 'center', textAlign: 'center' }}>
                {/* Round Red Icon Container */}
                <Box
                    sx={{
                        width: 80,
                        height: 80,
                        borderRadius: '50%',
                        backgroundColor: alpha(theme.palette.error.main, 0.08),
                        color: theme.palette.error.main,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        mb: 3.5
                    }}
                >
                    <DeleteFilled style={{ fontSize: '32px' }} />
                </Box>

                {/* Dialog Title */}
                <Typography
                    variant="h5"
                    sx={{
                        fontWeight: 600,
                        color: theme.palette.text.primary,
                        mb: 1.5,
                        fontSize: '20px'
                    }}
                >
                    {isMultiple ? 'Delete Files?' : 'Delete File?'}
                </Typography>

                {/* Dialog Subtitle & Content */}
                {isMultiple ? (
                    <>
                        <Typography
                            variant="subtitle2"
                            fontWeight={400}
                            sx={{
                                color: theme.palette.text.secondary,
                                fontSize: '15px',
                                mb: 4,
                            }}
                        >
                            Are you sure you want to delete this Files? This will permanently delete all files contained within it.
                        </Typography>

                        {/* Mixed Assets Card Block */}
                        <Box
                            sx={{
                                backgroundColor: theme.palette.mode === 'dark' ? theme.palette.background.default : theme.palette.grey[100],
                                borderRadius: '12px',
                                p: 1.5,
                                width: '100%',
                                display: 'flex',
                                alignItems: 'center',
                                gap: 2.5,
                                textAlign: 'left',
                                mb: 4
                            }}
                        >
                            <Box
                                sx={{
                                    backgroundColor: theme.palette.background.paper,
                                    border: `1px solid ${theme.palette.divider}`,
                                    borderRadius: '8px',
                                    width: 45,
                                    height: 45,
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    boxShadow: '0 2px 8px rgba(0, 0, 0, 0.04)',
                                    color: theme.palette.text.secondary,
                                    flexShrink: 0
                                }}
                            >
                                <MixedAssetsIcon />
                            </Box>

                            <Stack spacing={0.5} sx={{ overflow: 'hidden' }}>
                                <Typography
                                    variant="subtitle1"
                                    sx={{
                                        fontWeight: 500,
                                        color: theme.palette.text.primary,
                                        fontSize: '16px',
                                        whiteSpace: 'nowrap',
                                        overflow: 'hidden',
                                        textOverflow: 'ellipsis'
                                    }}
                                >
                                    Mixed Assets
                                </Typography>
                                <Typography
                                    variant="body2"
                                    sx={{
                                        fontWeight: 400,
                                        color: theme.palette.error.main,
                                        fontSize: '13px'
                                    }}
                                >
                                    {filesCount} files will be deleted
                                </Typography>
                            </Stack>
                        </Box>
                    </>
                ) : (
                    <>
                        <Typography
                            variant="body2"
                            sx={{
                                color: theme.palette.text.secondary,
                                fontSize: '15px',
                                lineHeight: 1.75,
                                mb: 1,
                                //maxWidth: '380px'
                            }}
                        >
                            Are you sure you want to delete{' '}
                            <Box component="span" sx={{ fontWeight: 500, color: theme.palette.text.primary }}>
                                {fileName}
                            </Box>{' '}
                            from the following path?
                        </Typography>

                        {/* File Path - plain breadcrumb with ellipsis on long names */}
                        <Box
                            sx={{
                                display: 'flex',
                                alignItems: 'center',
                                flexWrap: 'nowrap',
                                maxWidth: '100%',
                                mb: 1.5,
                                overflow: 'hidden'
                            }}
                        >
                            {(filePath ?? '').split(' > ').map((segment, index, arr) => (
                                <React.Fragment key={index}>
                                    <Tooltip title={segment.length > 18 ? segment : ''} placement="top" arrow>
                                        <Box
                                            component="span"
                                            sx={{
                                                fontSize: '15px',
                                                color: index === arr.length - 1
                                                    ? theme.palette.text.primary
                                                    : theme.palette.text.primary,
                                                fontWeight: 500,
                                                maxWidth: 150,
                                                overflow: 'hidden',
                                                textOverflow: 'ellipsis',
                                                whiteSpace: 'nowrap',
                                                flexShrink: index === arr.length - 1 ? 1 : 2,
                                                display: 'inline-block'
                                            }}
                                        >
                                            {segment}
                                        </Box>
                                    </Tooltip>
                                    {index < arr.length - 1 && (
                                        <Box
                                            component="span"
                                            sx={{
                                                mx: 0.5,
                                                fontSize: '13px',
                                                flexShrink: 0,
                                                mt: 0.2,
                                            }}
                                        >
                                            <RightOutlined style={{ color: theme.palette.text.primary, fontSize: '14px', fontWeight: 600 }} />
                                        </Box>
                                    )}
                                </React.Fragment>
                            ))}
                        </Box>

                        <Typography
                            variant="body1"
                            sx={{
                                color: theme.palette.text.secondary,
                                fontSize: '15px',
                                mb: 3,
                                fontWeight: 400
                            }}
                        >
                            This action cannot be undone.
                        </Typography>
                    </>
                )}

                <Divider sx={{ width: '100%', mb: 3 }} />

                {/* Action Buttons */}
                <Stack direction="row" spacing={2} sx={{ width: '100%', justifyContent: 'right' }}>

                    <Button
                        variant="outlined"
                        size="large"
                        onClick={onClose}
                        color="secondary"
                        sx={{
                            textTransform: 'none',
                            fontWeight: 500,
                            fontSize: '15px',
                            borderRadius: '4px',
                            px: 3,
                            py: 1,
                            flex: 1,
                            maxWidth: 120,
                            borderColor: 'secondary',
                            color: 'secondary',
                            '&:hover': {
                                borderColor: 'secondary',
                                color: 'secondary.dark'
                            }
                        }}
                    >
                        Cancel
                    </Button>

                    <Button
                        variant="contained"
                        size="large"
                        onClick={onConfirm}
                        sx={{
                            textTransform: 'none',
                            fontWeight: 500,
                            fontSize: '15px',
                            borderRadius: '4px',
                            px: 3,
                            py: 1.2,
                            flex: 1,
                            maxWidth: 180,
                            bgcolor: 'error.main',
                            '&:hover': { bgcolor: 'error.dark' },
                            boxShadow: 'none'
                        }}
                    >
                        Confirm Delete
                    </Button>
                </Stack>
            </DialogContent>
        </Dialog>
    );
}
